import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { Bell, CheckCheck, X, CheckCircle2, ClipboardList, Camera, Volume2, VolumeX, ShieldAlert } from 'lucide-react';
import api from '../utils/api';
import { playChime, supportsAudio, unlockAudio } from '../utils/notifySound';

// 管理员/代理系统消息铃铛：全局悬浮于右上角（管理员 TabBar 五格时自动下移）
// 普通用户动作（接量/完成任务/上传凭证）时实时弹出提醒，点击铃铛看历史列表
// 浏览器级系统通知（Notification API）：
// - 支持浏览器（Android Chrome 等）：请求权限后，收到新消息时弹系统横幅
// - 不支持（iOS Safari 等）：回退到页内气泡（下方 popup 弹条）
// 提示音：见 utils/notifySound.js（与 WorkspacePage 共用同一份实现）；音量默认开启，可点击铃铛旁喇叭静音
const supportsNotify = typeof window !== 'undefined' && 'Notification' in window;
const SND_KEY = 'dispatch_notify_sound';

function sndEnabled() {
  try { return localStorage.getItem(SND_KEY) !== '0'; } catch { return true; }
}


export default function AdminNotificationBell({ offsetTop = 8 }) {
  const { subscribe } = useSocket();
  const [list, setList] = useState([]);
  const [popup, setPopup] = useState(null); // 右上角实时弹条
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [notifyPerm, setNotifyPerm] = useState(supportsNotify ? Notification.permission : null);
  const [soundOn, setSoundOn] = useState(sndEnabled);
  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const popupTimer = useRef(null);

  const fetchList = useCallback(() => {
    api.get('/notifications/admin').then(res => setList(res.data.notifications || [])).catch(() => {});
  }, []);

  // 首次进入拉一次历史
  useEffect(() => {
    if (!loaded) { fetchList(); setLoaded(true); }
    return () => { if (popupTimer.current) clearTimeout(popupTimer.current); };
    // eslint-disable-next-line
  }, [loaded, fetchList]);

  // 首次用户手势时解锁音频（页面任意点击/触摸均触发，{ once } 自动移除）
  useEffect(() => {
    document.addEventListener('click',    unlockAudio, { once: true });
    document.addEventListener('touchend', unlockAudio, { once: true });
    return () => {
      document.removeEventListener('click',    unlockAudio);
      document.removeEventListener('touchend', unlockAudio);
    };
  }, []);

  // 请求浏览器通知权限（需用户手势触发）
  const requestNotify = () => {
    if (!supportsNotify) return;
    Notification.requestPermission().then(p => setNotifyPerm(p)).catch(() => {});
  };

  // 提示音开关（记忆到 localStorage，点铃铛旁喇叭切换）
  const toggleSound = () => {
    unlockAudio(); // 在用户手势中解锁单例 AudioContext，后续 socket 事件可直接播放
    setSoundOn(prev => {
      const next = !prev;
      try { localStorage.setItem(SND_KEY, next ? '1' : '0'); } catch {}
      if (next) playChime(); // 开启时试播一下让用户确认
      return next;
    });
  };

  // 弹系统通知横幅（Android Chrome 等），失败静默回退页内气泡
  // 仅在页面不可见时才触发：前台已有页内 popup，不需要再叠一个系统横幅
  // 注意：安卓 Chrome 禁止直接 new Notification()，必须走 SW 的 showNotification()
  const fireBrowserNotify = useCallback((payload) => {
    if (!supportsNotify || Notification.permission !== 'granted') return;
    if (!document.hidden) return;
    // 优先走 Service Worker（安卓 Chrome 只认这条路）
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.showNotification(payload.title || '系统消息', {
          body: payload.content || ' ', // 空字符串在部分机型会降级为静默，用空格兜底
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'dispatch-admin-notify', // 同类通知覆盖显示，不叠加
          renotify: true,
          data: { url: '/admin' },
        });
      }).catch(() => {
        // SW 不可用时回退：直接 new Notification（iOS Safari / 桌面浏览器）
        try {
          const n = new Notification(payload.title || '系统消息', {
            body: payload.content || '',
            tag: 'dispatch-admin-notify',
          });
          n.onclick = () => { window.focus(); setOpen(true); try { n.close(); } catch {} };
          setTimeout(() => { try { n.close(); } catch {} }, 8000);
        } catch (_) { /* 不支持时静默 */ }
      });
    } else {
      // 老式桌面浏览器：直接 new Notification
      try {
        const n = new Notification(payload.title || '系统消息', {
          body: payload.content || '',
          tag: 'dispatch-admin-notify',
        });
        n.onclick = () => { window.focus(); setOpen(true); try { n.close(); } catch {} };
        setTimeout(() => { try { n.close(); } catch {} }, 8000);
      } catch (_) { /* 不支持时静默 */ }
    }
  }, []);

  // 实时事件：写入列表 + 右上角弹条（3.5s 自动消失）+ 提示音 + 浏览器系统通知
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event, payload) => {
      if (event !== 'admin_notification') return;
      if (payload && payload.title) {
        setList(prev => {
          if (prev.some(n => n.id === payload.id)) return prev; // 断线重连时防重复
          return [payload, ...prev].slice(0, 50);
        });
        setPopup(payload);
        if (popupTimer.current) clearTimeout(popupTimer.current);
        popupTimer.current = setTimeout(() => setPopup(null), 3500);
        if (soundOn && supportsAudio) playChime();
        fireBrowserNotify(payload);
      }
    });
  }, [subscribe, fireBrowserNotify, soundOn]);

  // 点击面板外部关闭
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          bellRef.current && !bellRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [open]);

  const unread = list.filter(n => !n.is_read).length;

  const markAllRead = async () => {
    setList(prev => prev.map(n => ({ ...n, is_read: 1 })));
    try { await api.post('/notifications/admin/read-all'); } catch {}
  };

  const toggle = () => {
    setOpen(v => !v);
    if (!open && !loaded) { fetchList(); setLoaded(true); }
  };

  const closeOpen = () => setOpen(false);

  // 左侧强调条颜色：完成任务绿色 / 接量蓝 / 上传图片紫
  const accent = (type) => {
    if (type === 'task_completed') return '#10b981';
    if (type === 'task_requested') return '#0a84ff';
    if (type === 'image_uploaded') return '#8e5cf6';
    if (type === 'puzzle_manual_fill') return '#ff9500';
    return '#f5a623';
  };
  const Icon = (type) => {
    if (type === 'task_completed') return <CheckCircle2 size={15} color="#10b981" />;
    if (type === 'task_requested') return <ClipboardList size={15} color="#0a84ff" />;
    if (type === 'image_uploaded') return <Camera size={15} color="#8e5cf6" />;
    if (type === 'puzzle_manual_fill') return <ShieldAlert size={15} color="#ff9500" />;
    return <Bell size={15} />;
  };

  const flag = (type) => {
    if (type === 'task_completed') return '完成任务';
    if (type === 'task_requested') return '提交接量';
    if (type === 'image_uploaded') return '上传凭证';
    if (type === 'puzzle_manual_fill') return '手填待核实';
    return '系统';
  };

  return (
    <>
      {/* 实时弹条（右上角，3.5s 自动消失） */}
      {popup && (
        <div style={{
          position: 'fixed',
          top: `calc(env(safe-area-inset-top, 0px) + ${offsetTop + 44}px)`,
          right: 12,
          zIndex: 9998,
          width: 'min(320px, calc(100vw - 24px))',
          background: '#1e293b',
          color: '#fff',
          borderRadius: 14,
          padding: '12px 14px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          cursor: 'pointer',
          animation: 'fadeIn 0.25s ease',
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
        }}
        onClick={() => { setPopup(null); toggle(); }}
        >
          <div style={{ width: 6, alignSelf: 'stretch', borderRadius: 3, background: accent(popup.type), flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.4 }}>{popup.title}</div>
            {popup.content && (
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3, lineHeight: 1.5 }}>{popup.content}</div>
            )}
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>点击查看全部</div>
          </div>
        </div>
      )}

      {/* 铃铛按钮 + 提示音开关 */}
      <div ref={bellRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2 }}>
        <button
          onClick={toggleSound}
          title={soundOn ? '关闭提示音' : '开启提示音'}
          style={{
            background: 'none', border: 'none', padding: 4, cursor: 'pointer',
            color: soundOn ? 'var(--c-primary)' : 'var(--c-text-tertiary, #94a3b8)',
            display: 'flex', alignItems: 'center',
          }}
          aria-label={soundOn ? '关闭提示音' : '开启提示音'}
        >
          {soundOn ? <Volume2 size={17} strokeWidth={2} /> : <VolumeX size={17} strokeWidth={2} />}
        </button>
        <button
          onClick={toggle}
          style={{
            background: 'none', border: 'none', padding: 4, cursor: 'pointer',
            color: 'var(--c-primary)', display: 'flex', alignItems: 'center',
            position: 'relative',
          }}
          aria-label="系统消息"
        >
          <Bell size={19} strokeWidth={2} />
          {unread > 0 && (
            <span style={{
              position: 'absolute', top: -2, right: -4,
              minWidth: 16, height: 16, borderRadius: 8,
              background: '#ff3b30', color: '#fff',
              fontSize: 10, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 4px',
            }}>
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>

        {/* 下拉面板 */}
        {open && (
          <div ref={panelRef} style={{
            position: 'fixed',
            top: `calc(env(safe-area-inset-top, 0px) + ${offsetTop + 40}px)`,
            right: 12,
            width: 'min(340px, calc(100vw - 24px))',
            maxHeight: '65vh',
            background: 'var(--c-surface, #fff)',
            borderRadius: 16,
            boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
            border: '0.5px solid var(--c-border)',
            zIndex: 9997,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 14px', borderBottom: '0.5px solid var(--c-border)', flexShrink: 0,
            }}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>系统消息</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {supportsNotify && notifyPerm !== 'granted' && (
                  <button onClick={requestNotify} style={{
                    background: 'var(--c-primary-light, rgba(0,122,255,0.1))',
                    border: 'none', cursor: 'pointer',
                    fontSize: 11, color: 'var(--c-primary)', fontWeight: 600,
                    borderRadius: 999, padding: '3px 10px',
                  }}>
                    {notifyPerm === 'denied' ? '通知已关闭' : '开启通知'}
                  </button>
                )}
                <button onClick={markAllRead} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 12, color: 'var(--c-primary)', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 3,
                }}>
                  <CheckCheck size={14} /> 全部已读
                </button>
              </div>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {list.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--c-text-secondary)', fontSize: 13, padding: '30px 12px' }}>
                  暂无消息，用户完成/接量/上传图片时会在这里提醒
                </div>
              ) : list.map(n => (
                <div key={n.id} style={{
                  padding: '11px 14px',
                  borderBottom: '0.5px solid var(--c-divider)',
                  display: 'flex', gap: 8,
                  background: n.is_read ? 'transparent' : 'rgba(0,122,255,0.04)',
                }}>
                  <div style={{ marginTop: 2, flexShrink: 0 }}>{Icon(n.type)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
                    </div>
                    {n.content && (
                      <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2, lineHeight: 1.5 }}>{n.content}</div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 4, display: 'flex', gap: 6 }}>
                      <span style={{ color: accent(n.type), fontWeight: 600 }}>{flag(n.type)}</span>
                      <span>{n.created_at}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button onClick={closeOpen} style={{
              flexShrink: 0, padding: '10px', background: 'none', border: 'none',
              borderTop: '0.5px solid var(--c-border)', cursor: 'pointer',
              color: 'var(--c-text-secondary)', fontSize: 12, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            }}>
              <X size={14} /> 关闭
            </button>
          </div>
        )}
      </div>
    </>
  );
}